import { apiFetch } from './client'

export interface PlanUsageResource {
  key: string
  label: string
  current: number
  limit: number
}

export interface PlanUsage {
  enforcement: boolean
  resources: PlanUsageResource[]
}

export async function getPlanUsage(): Promise<PlanUsage> {
  return apiFetch<PlanUsage>('/config/plan-usage')
}

export interface ConfigRow {
  key: string
  value: string
  type: 'string' | 'integer' | 'boolean' | 'json'
  label: string
  group: string
  editable: boolean
  updated_at: string
}

export interface PublicConfig {
  business_name: string
  business_logo_url: string
}

export async function getPublicConfig(): Promise<PublicConfig> {
  return apiFetch<PublicConfig>('/config/public')
}

export interface BillingInfo {
  enforcement: boolean
  in_grace_period: boolean
  billing_start: string | null
  plan: {
    base_price_cop: number
    included_devices: number
    included_members: number
    included_users: number
  }
  prices: {
    extra_device_cop: number
    extra_members_block_cop: number
    extra_user_cop: number
  }
  usage: Array<{ key: string; label: string; current: number; limit: number }>
  overage: {
    extra_devices: number
    extra_member_blocks: number
    extra_users: number
  }
  costs: {
    base: number
    extra_devices: number
    extra_members: number
    extra_users: number
    total: number
  }
}

export async function getBillingInfo(): Promise<BillingInfo> {
  return apiFetch<BillingInfo>('/config/billing')
}

export interface Invoice {
  id: string
  period_start: string
  period_end: string
  due_date: string
  total_cop: number
  status: 'pending' | 'paid' | 'overdue' | 'void'
  paid_at: string | null
  notes: string | null
  created_at: string
}

export async function getInvoices(): Promise<{ invoices: Invoice[] }> {
  return apiFetch<{ invoices: Invoice[] }>('/config/invoices')
}

export async function markInvoicePaid(
  id: string,
  opts?: { notes?: string; total_cop?: number },
): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/config/invoices/${id}/paid`, {
    method: 'PATCH',
    body: JSON.stringify(opts ?? {}),
  })
}

export async function getAllConfig(): Promise<{ config: ConfigRow[] }> {
  return apiFetch<{ config: ConfigRow[] }>('/config')
}

export async function updateConfig(updates: Record<string, string>, opToken?: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/config', {
    method: 'PATCH',
    headers: opToken ? { 'X-Op-Token': opToken } : undefined,
    body: JSON.stringify(updates),
  })
}

export interface TotpSetup {
  qr_base64: string
  otpauth_uri: string
  issuer: string
  account: string
}

export async function getTotpSetup(): Promise<TotpSetup> {
  return apiFetch<TotpSetup>('/totp/setup')
}

export async function confirmTotpSetup(code: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/totp/setup/confirm', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

export async function verifyTotp(code: string): Promise<{ op_token: string }> {
  return apiFetch<{ op_token: string }>('/totp/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}
